import apiClient from './apiClient';
import { endpoints } from '../config/apiConfig';
import axios from 'axios';

const API_CONFIG = { endpoints };

// Creator services
export const creatorService = {
  /**
   * Get all creators with optional projection
   * @param {Object} params - Query parameters
   * @param {string} params.projection - Projection type: 'MINIMAL', 'LIST', 'WORKLOAD', 'ANALYTICS', 'PROFILE', 'FULL'
   * @returns {Promise<Array>} Array of creators
   *
   * Projection Usage:
   * - MINIMAL: Dropdowns, references (ID, username, twitterHandle)
   * - LIST: Table views, basic lists (adds status, followers, campaigns)
   * - WORKLOAD: Workload calculations (campaigns, workloadLevel)
   * - ANALYTICS: Analytics dashboards (metrics, performance data)
   * - PROFILE: Detailed profile views (full creator data)
   * - FULL: Everything (use sparingly)
   */
  getCreators: async (params = {}) => {
    // Default limit to 5000 to load all creators (production has ~741 creators)
    // This ensures we don't rely on backend defaults which may be lower
    const { projection = 'LIST', limit = 5000, ...otherParams } = params;
    console.log('Fetching all creators with projection:', projection, 'limit:', limit);
    const response = await apiClient.get(endpoints.creators.base, {
      params: { projection, limit, ...otherParams }
    });

    // Handle different response formats to ensure consistent data structure
    let creatorsData = [];
    if (Array.isArray(response.data)) {
      // Direct array response
      creatorsData = response.data;
    } else if (response.data && response.data.creators && Array.isArray(response.data.creators)) {
      // Paginated response format { creators: [...], pagination: {...} }
      creatorsData = response.data.creators;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      // Nested data property
      creatorsData = response.data.data;
    } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
      // Single object response
      creatorsData = [response.data];
    }

    return creatorsData;
  },

  /**
   * Get creator by ID with optional projection
   * @param {string} id - Creator ID
   * @param {Object} options - Optional parameters
   * @param {string} options.projection - Projection type (default: 'PROFILE')
   * @returns {Promise<Object>} Creator data
   */
  getCreatorById: async (id, options = {}) => {
    const { projection = 'PROFILE' } = options;
    console.log('Fetching creator by ID:', id, 'with projection:', projection);
    const response = await apiClient.get(endpoints.creators.byId(id), {
      params: { projection }
    });
    return response.data;
  },

  findCreatorByHandle: async (handle) => {
    const response = await apiClient.get('/creators/lookup-handle', { params: { handle } });
    return response.data.creator;
  },

  getNotesQueue: async (params = {}) => {
    const response = await apiClient.get('/creators/notes-queue', { params });
    return response.data;
  },

  getCreatorNotes: async (id) => {
    const response = await apiClient.get(`/creators/${encodeURIComponent(id)}/notes`);
    return response.data;
  },
  
  createCreator: async (data) => {
    const response = await apiClient.post(endpoints.creators.base, data);
    return response.data;
  },
  
  deleteCreator: async (id) => {
    console.log('Deleting creator:', id);
    const response = await apiClient.delete(endpoints.creators.byId(id));
    console.log('Delete creator response:', response);
    return response.data;
  },
  
  updateCreator: async (id, data) => {
    console.log('Updating creator:', id, { fields: Object.keys(data) });
    const response = await apiClient.patch(endpoints.creators.byId(id), data);
    return response.data;
  },

  setTelegramNotificationOverride: async (id, chatId) => {
    const response = await apiClient.patch(
      `/creators/${encodeURIComponent(id)}/telegram-notification-override`,
      { chatId }
    );
    return response.data;
  },

  sendTelegramNotificationTest: async (id) => {
    const response = await apiClient.post(
      `/creators/${encodeURIComponent(id)}/telegram-notification-test`
    );
    return response.data;
  },

  getUnmatchedTelegramGroups: async (status = 'active') => {
    const response = await apiClient.get('/creators/telegram-notification-groups', {
      params: { status, assignment: 'unmatched', limit: 100 }
    });
    return response.data;
  },
  
  uploadCreatorFile: async (formData) => {
    console.log('Uploading creator file...');
    // Don't set Content-Type header - axios will set it automatically with the correct boundary
    const response = await apiClient.post('/upload/upload', formData);
    console.log('Upload creator file response:', response);
    return response;
  },
  
  get: async (endpoint) => {
    console.log(`Getting data from endpoint: ${endpoint}`);
    const response = await apiClient.get(`/${endpoint}`);
    console.log(`Response from ${endpoint}:`, response);
    return response;
  },

  post: async (endpoint, data = {}) => {
    console.log(`Posting to endpoint: ${endpoint}`, data);
    const response = await apiClient.post(`/${endpoint}`, data);
    console.log(`Response from ${endpoint}:`, response);
    return response;
  },

  deleteAllCreators: async () => {
    console.log('Deleting all creators...');
    const response = await apiClient.delete('/creators/delete-all');
    console.log('Delete all creators response:', response);
    return response;
  },

  // Sync Twitter followers for all creators
  syncCreatorFollowers: async (creatorId) => {
    console.log('Syncing followers for creator:', creatorId);
    const response = await apiClient.post(`/twitter/sync/followers/${creatorId}`);
    console.log('Sync followers response:', response);
    return response.data;
  },

  // Historical data endpoints (Week 8 MongoDB optimization)
  /**
   * Get follower history for a creator
   * @param {string} id - Creator ID
   * @param {Object} params - Query parameters (limit, startDate, endDate)
   * @returns {Promise<Array>} Array of follower history records
   */
  getFollowersHistory: async (id, params = {}) => {
    console.log('Fetching followers history for creator:', id);
    const response = await apiClient.get(endpoints.creators.followersHistory(id), { params });
    console.log('Followers history response:', response);
    return response.data;
  },

  /**
   * Get message history for a creator
   * @param {string} id - Creator ID
   * @param {Object} params - Query parameters (limit, messageType, startDate, endDate)
   * @returns {Promise<Array>} Array of message records
   */
  getMessages: async (id, params = {}) => {
    console.log('Fetching messages for creator:', id);
    const response = await apiClient.get(endpoints.creators.messages(id), { params });
    console.log('Messages response:', response);
    return response.data;
  },

  /**
   * Get transaction history for a creator
   * @param {string} id - Creator ID
   * @param {Object} params - Query parameters (limit, startDate, endDate)
   * @returns {Promise<Array>} Array of transaction records
   */
  getTransactions: async (id, params = {}) => {
    console.log('Fetching transactions for creator:', id);
    const response = await apiClient.get(endpoints.creators.transactions(id), { params });
    console.log('Transactions response:', response);
    return response.data;
  },

  /**
   * Get campaign history for a creator
   * @param {string} id - Creator ID
   * @param {Object} params - Query parameters (limit, status)
   * @returns {Promise<Array>} Array of campaign history records
   */
  getCampaignHistory: async (id, params = {}) => {
    console.log('Fetching campaign history for creator:', id);
    const response = await apiClient.get(endpoints.creators.campaigns(id), { params });
    console.log('Campaign history response:', response);
    return response.data;
  },

  /**
   * Get analytics snapshots for a creator
   * @param {string} id - Creator ID
   * @param {Object} params - Query parameters (limit, snapshotType, startDate, endDate)
   * @returns {Promise<Array>} Array of analytics snapshot records
   */
  getAnalyticsSnapshots: async (id, params = {}) => {
    console.log('Fetching analytics snapshots for creator:', id);
    const response = await apiClient.get(endpoints.creators.analyticsSnapshots(id), { params });
    console.log('Analytics snapshots response:', response);
    return response.data;
  }
};

// Brand services
export const brandService = {
  getAllBrands: async () => {
    const response = await apiClient.get(endpoints.brands.base);
    return response.data;
  },
  
  createBrand: async (data) => {
    const response = await apiClient.post(endpoints.brands.base, data);
    return response.data;
  },
  
  getBrandById: async (id) => {
    const response = await apiClient.get(endpoints.brands.byId(id));
    return response.data;
  }
};

// Campaign services
export const campaignService = {
  getAllCampaigns: async () => {
    const response = await apiClient.get(endpoints.campaigns.base);
    return response.data;
  },
  
  createCampaign: async (data) => {
    const response = await apiClient.post(endpoints.campaigns.base, data);
    return response.data;
  },
  
  archiveCampaign: async (id) => {
    console.log(`Archiving campaign with ID: ${id}`);
    const response = await apiClient.delete(endpoints.campaigns.delete(id));
    console.log('Archive campaign response:', response);
    return response.data;
  },
  
  finishCampaign: async (id) => {
    console.log(`Finishing campaign with ID: ${id}`);
    const response = await apiClient.put(`${endpoints.campaigns.base}/${id}/finish`);
    console.log('Finish campaign response:', response);
    return response.data;
  },
  
  updateCampaignStatus: async (id, status) => {
    const response = await apiClient.post(endpoints.campaigns.status(id), { status });
    return response.data;
  },
  
  getCampaignCreators: async (id) => {
    const response = await apiClient.get(endpoints.campaigns.creators(id));
    return response.data;
  },
  
  getRemovedCreators: async (id) => {
    const response = await apiClient.get(endpoints.campaigns.removedCreators(id));
    return response.data;
  },
  
  removeCreator: async (campaignId, creatorId) => {
    const response = await apiClient.delete(endpoints.campaigns.removeCreator(campaignId, creatorId));
    return response.data;
  },
  
  restoreCreator: async (campaignId, creatorId) => {
    const response = await apiClient.post(endpoints.campaigns.restoreCreator(campaignId, creatorId));
    return response.data;
  },

  getPublicReportConfig: async (id) => {
    const response = await apiClient.get(endpoints.campaigns.publicReport(id));
    return response.data;
  },

  updatePublicReportConfig: async (id, data) => {
    const response = await apiClient.put(endpoints.campaigns.publicReport(id), data);
    return response.data;
  }
};

// Twitter services
export const twitterService = {
  getUserByUsername: async (username) => {
    const response = await apiClient.get(endpoints.twitter.userByUsername(username));
    return response.data;
  },
  
  getFollowersByUserId: async (id) => {
    const response = await apiClient.get(endpoints.twitter.followersByUserId(id));
    return response.data;
  }
};

// Auth services
export const authService = {
  verify: async () => {
    const response = await apiClient.get(endpoints.auth.verify);
    return response.data;
  },
  
  login: async (credentials) => {
    const response = await apiClient.post(endpoints.auth.login, credentials);
    return response.data;
  }
};

// Reporting services
export const reportingService = {
  getCampaignReports: async () => {
    const response = await apiClient.get(endpoints.reporting.campaigns);
    return response.data;
  },
  
  getCampaignReport: async (campaignId) => {
    const response = await apiClient.get(endpoints.reporting.campaignDetail(campaignId));
    return response.data;
  },
  
  updateCampaignStage: async (campaignId, stage) => {
    const response = await apiClient.put(endpoints.reporting.updateStage(campaignId), { stage });
    return response.data;
  }
};

// Admin services
export const adminService = {
  // Twitter AI service configuration
  getTwitterServiceConfig: async () => {
    const response = await apiClient.get(endpoints.admin.twitterServiceConfig);
    return response.data;
  },
  
  updateTwitterServiceConfig: async (config) => {
    const response = await apiClient.post(endpoints.admin.twitterServiceConfig, config);
    return response.data;
  },
  
  testTwitterServiceConnection: async () => {
    const response = await apiClient.get(endpoints.admin.testTwitterService);
    return response.data;
  },
  
  getTwitterServiceStats: async () => {
    const response = await apiClient.get(endpoints.admin.twitterServiceStats);
    return response.data;
  },
  
  getSocketStats: async () => {
    const response = await apiClient.get('/admin/socket-stats');
    return response;
  },

  getActiveConnections: async () => {
    const response = await apiClient.get('/admin/active-connections');
    return response;
  },

  emitTestEvent: async (eventData) => {
    const response = await apiClient.post('/admin/emit-test-event', eventData);
    return response;
  }
};

// Default export as a single object with all services
export default {
  creators: creatorService,
  brands: brandService,
  campaigns: campaignService,
  twitter: twitterService,
  auth: authService,
  admin: adminService
};
